import { withStyles } from 'vopay';
import { Button } from 'UI';
import styles from './MsEntraButton.module.scss';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

import MSLogo from './MsLogo';
import { useEffect } from 'react';

const cn = withStyles(styles);

export type LoginResult = { email: string; accessToken: string };

type MSEntraButtonProps = {
  onOpen: (open: boolean) => void;
  onLogin: (res: LoginResult) => void;
};

export default function MSEntraButton({ onOpen, onLogin }: MSEntraButtonProps) {
  const { instance: msalInstance, inProgress } = useMsal();
  const usingMs = inProgress === InteractionStatus.Login || inProgress === InteractionStatus.AcquireToken;
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Login' });

  useEffect(() => {
    onOpen(usingMs);
  }, [onOpen, usingMs]);

  async function handleLogin() {
    try {
      const res = await msalInstance.loginPopup();
      // MSAL types account as nullable, and it is genuinely absent when the
      // popup resolves without a completed account selection.
      const email = res?.account?.username;

      if (!email) {
        alertDialog.error(
          t('SSO.errorTitle', { defaultValue: 'Error' }),
          t('SSO.noAccountError', {
            defaultValue: 'Microsoft did not return an account for this sign-in. Please try again.',
          }),
        );

        return;
      }

      onLogin({ email, accessToken: res.accessToken });
    } catch (err) {
      if (err instanceof BrowserAuthError && err.errorCode === 'user_cancelled') {
        // User closed the popup, don't treat as error
        console.log('User cancelled login');

        return;
      } else {
        console.error(err);

        if (err instanceof Error) {
          alertDialog.error(t('SSO.errorTitle', { defaultValue: 'Error' }), err.message);
        }
      }
    }
  }

  return (
    <Button
      id="sso-microsoft"
      size="large"
      type="button"
      className={cn('msEntraButton')}
      onClick={handleLogin}
      disabled={usingMs}
    >
      <span className={cn('gridItem', 'logo')} aria-hidden="true">
        <MSLogo />
      </span>

      <span className={cn('gridItem')}>{t('SSO.microsoftButton', { defaultValue: 'Continue with Microsoft' })}</span>
    </Button>
  );
}
